import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  BookOpen, 
  Users, 
  DollarSign, 
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  FileText,
  ShoppingCart,
  BarChart3,
  Upload,
  BookPlus,
  Shield,
  ShieldCheck,
  Package,
  MessageCircle
} from 'lucide-react';
import { booksService, usersService, salesService, enhancedAdminService } from '../services/api';
import { Link } from 'react-router-dom';
import { useAuth } from '../../src/context/AuthContext';

const StatCard = ({ title, value, icon: Icon, color, subtitle, loading }) => (
  <div className="bg-white rounded-2xl shadow-xs border border-gray-100 p-6 hover:shadow-md transition-all duration-300 hover:translate-y-[-2px] group">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
        <div className="flex items-baseline justify-between">
          <p className="text-2xl font-bold text-gray-900">
            {loading ? (
              <div className="h-8 w-20 bg-gray-200 rounded-lg animate-pulse"></div>
            ) : (
              value
            )}
          </p>
        </div>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-2 font-medium">{subtitle}</p>
        )}
      </div>
      <div className={`p-3 rounded-xl ${color} bg-opacity-10 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className={`h-6 w-6 ${color}`} />
      </div>
    </div>
  </div>
);

const RecentBookCard = ({ book }) => {
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/book-placeholder.png';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.silkspace.co';
    return `${baseUrl}${imagePath}`;
  };

  return (
    <div className="flex items-center justify-between p-4 hover:bg-gray-50 transition-all duration-200 rounded-xl group border border-transparent hover:border-gray-200">
      <div className="flex items-center space-x-4 flex-1 min-w-0">
        <div className="relative flex-shrink-0">
          <img
            src={getImageUrl(book.cover_image)}
            alt={book.title}
            className="h-14 w-10 object-cover rounded-lg shadow-xs border border-gray-200 group-hover:shadow-md transition-shadow duration-200"
          />
          {book.status === 'pending' && (
            <div className="absolute -top-1 -right-1">
              <div className="bg-yellow-500 rounded-full p-1 shadow-xs border border-white">
                <Clock className="h-2 w-2 text-white" />
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{book.title}</h3>
          <p className="text-xs text-gray-500 truncate mt-1">by {book.author_name || book.author_details?.username || 'Unknown Author'}</p>
          <div className="flex items-center space-x-3 mt-2">
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full font-medium">{book.genre_name}</span>
            <span className="text-xs font-semibold text-gray-700">GHS {book.price || '0.00'}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-3 flex-shrink-0">
        {book.status === 'approved' && <CheckCircle className="h-4 w-4 text-green-500" />}
        {book.status === 'pending' && <Clock className="h-4 w-4 text-yellow-500" />}
        {book.status === 'rejected' && <XCircle className="h-4 w-4 text-red-500" />}
        <span className={`text-xs font-medium px-3 py-1.5 rounded-full border ${
          book.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' :
          book.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
          'bg-red-50 text-red-700 border-red-200'
        }`}>
          {book.status?.charAt(0).toUpperCase() + book.status?.slice(1) || 'Unknown'}
        </span>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { data: stats, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => salesService.getStats(),
    refetchInterval: 30000,
  });

  const { data: books, isLoading: booksLoading, error: booksError, refetch: refetchBooks } = useQuery({
    queryKey: ['books'],
    queryFn: () => booksService.getAll(),
    refetchInterval: 30000,
  });

  const { data: users, isLoading: usersLoading, error: usersError, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
    refetchInterval: 30000,
  });

  const { data: pendingBooks } = useQuery({
    queryKey: ['pending-books'],
    queryFn: () => booksService.getPending(),
  });

  const { data: musicStats } = useQuery({
    queryKey: ['music-earnings-summary'],
    queryFn: () => enhancedAdminService.getMusicEarnings?.() || Promise.resolve(null),
    refetchInterval: 60000,
    retry: 1,
  });

  const isLoading = statsLoading || booksLoading || usersLoading;
  const hasError = statsError || booksError || usersError;

  // Check user role
  const isSuperAdmin = user?.is_superuser;
  const isStaff = user?.user_type === 'admin' && user?.is_staff && !user?.is_superuser;

  const booksData = books?.results || books || [];
  const usersData = users?.results || users || [];
  const pendingBooksData = pendingBooks || [];

  const totalAuthors = usersData.filter(user => user.user_type === 'author').length;
  const totalReaders = usersData.filter(user => user.user_type === 'reader').length;
  const totalStaff = usersData.filter(user => user.user_type === 'admin' && !user.is_superuser).length;

  // Base stats that everyone sees
  const baseStats = [
    {
      title: 'Total Books',
      value: booksData.length,
      icon: BookOpen,
      color: 'text-[#1d1d1d]',
      subtitle: `${pendingBooksData.length} pending review`,
      loading: booksLoading,
      roles: ['superadmin', 'staff']
    },
    {
      title: 'Active Authors',
      value: totalAuthors,
      icon: Users,
      color: 'text-purple-600',
      subtitle: 'Registered creators',
      loading: usersLoading,
      roles: ['superadmin', 'staff']
    },
    {
      title: 'Total Readers',
      value: totalReaders,
      icon: Users,
      color: 'text-green-600',
      subtitle: 'Active users',
      loading: usersLoading,
      roles: ['superadmin', 'staff']
    },
  ];

  // Superadmin-only stats
  const superAdminStats = [
    {
      title: 'Total Revenue',
      value: `GHS ${stats?.total_revenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'text-emerald-600',
      subtitle: 'All time sales',
      loading: statsLoading,
      roles: ['superadmin']
    },
    {
      title: 'Platform Earnings',
      value: `GHS ${stats?.platform_earnings?.toFixed(2) || '0.00'}`,
      icon: TrendingUp,
      color: 'text-orange-600',
      subtitle: 'Commission revenue',
      loading: statsLoading,
      roles: ['superadmin']
    },
    {
      title: 'Total Sales',
      value: stats?.total_sales || 0,
      icon: ShoppingCart,
      color: 'text-indigo-600',
      subtitle: 'Book purchases',
      loading: statsLoading,
      roles: ['superadmin']
    },
    {
      title: 'Staff Members',
      value: totalStaff,
      icon: Shield,
      color: 'text-blue-600',
      subtitle: 'Admin team',
      loading: usersLoading,
      roles: ['superadmin']
    }
  ];

  // Music stats (superadmin)
  const musicStatCards = musicStats ? [
    {
      title: 'Music Revenue',
      value: `GHS ${(musicStats.total_music_revenue || 0).toFixed(2)}`,
      icon: BarChart3,
      color: 'text-pink-600',
      subtitle: `${musicStats.total_music_sales || 0} track sales`,
      loading: false,
      roles: ['superadmin']
    },
    {
      title: 'Music Platform Cut',
      value: `GHS ${(musicStats.platform_cut || 0).toFixed(2)}`,
      icon: TrendingUp,
      color: 'text-orange-500',
      subtitle: '15% commission',
      loading: false,
      roles: ['superadmin']
    },
    {
      title: 'Artist Earnings Pending',
      value: `GHS ${(musicStats.pending_artist_payouts || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'text-yellow-600',
      subtitle: 'Awaiting payout',
      loading: false,
      roles: ['superadmin']
    },
  ] : [];

  // Staff-only stats
  const staffStats = [
    {
      title: 'Pending Services',
      value: '0', // You can replace with actual service count
      icon: Package,
      color: 'text-indigo-600',
      subtitle: 'Service requests',
      loading: false,
      roles: ['staff']
    },
    {
      title: 'Open Tickets',
      value: '0', // You can replace with actual ticket count
      icon: MessageCircle,
      color: 'text-red-600',
      subtitle: 'Support tickets',
      loading: false,
      roles: ['staff']
    }
  ];

  // Filter stats based on user role
  const userRole = isSuperAdmin ? 'superadmin' : (isStaff ? 'staff' : 'none');
  const allStats = [...baseStats, ...(isSuperAdmin ? superAdminStats : []), ...(isSuperAdmin ? musicStatCards : []), ...(isStaff ? staffStats : [])];
  const filteredStats = allStats.filter(stat => stat.roles.includes(userRole));

  const recentBooks = booksData.slice(0, 5);

  const handleRefresh = () => {
    refetchStats();
    refetchBooks();
    refetchUsers();
  };

  if (hasError) {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-600 mb-6">We couldn't load the dashboard data. Please check your connection and try again.</p>
            <button
              onClick={handleRefresh}
              className="bg-[#1d1d1d] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                isSuperAdmin ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {isSuperAdmin ? 'Super Admin' : 'Staff'}
              </span>
            </div>
            <p className="text-gray-600 font-medium">
              {isSuperAdmin 
                ? "Welcome back! Here's what's happening with your platform today."
                : "Welcome back! Here's your task overview for today."}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/upload-book"
              className="inline-flex items-center px-5 py-3 bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <BookPlus className="h-4 w-4 mr-2" />
              Upload Book
            </Link>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center px-5 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-all duration-200 shadow-xs hover:shadow-md"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Welcome Banner based on role */}
      {isStaff && (
        <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">👋 Welcome, {user?.first_name || user?.username}!</h3>
              <p className="text-gray-600">
                You're logged in as a Staff member. You have access to manage books, services, and support tickets.
                {!isSuperAdmin && " Financial data and user management are restricted to Super Admins."}
              </p>
            </div>
            <Shield className="h-10 w-10 text-blue-400" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {filteredStats.map((stat, index) => (
          <StatCard
            key={index}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
            subtitle={stat.subtitle}
            loading={stat.loading}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-xs border border-gray-200">
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Books</h2>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium">
                  {booksData.length} total
                </span>
                <Link 
                  to="/books" 
                  className="text-[#1d1d1d] hover:text-gray-700 text-sm font-semibold transition-colors duration-200 no-underline"
                >
                  View all →
                </Link>
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {recentBooks.length > 0 ? (
              recentBooks.map((book) => (
                <RecentBookCard key={book.id} book={book} />
              ))
            ) : (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No books yet</h3>
                <p className="text-gray-500 font-medium">Books will appear here once uploaded</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-xs border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Pending Reviews</h2>
                {pendingBooksData.length > 0 && (
                  <span className="bg-yellow-100 text-yellow-800 text-sm font-semibold px-3 py-1.5 rounded-full border border-yellow-200">
                    {pendingBooksData.length} waiting
                  </span>
                )}
              </div>
            </div>
            <div className="p-6">
              {pendingBooksData.length > 0 ? (
                <div className="space-y-4">
                  {pendingBooksData.slice(0, 3).map((book) => (
                    <Link 
                      key={book.id}
                      to="/books"
                      className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl border border-yellow-200 hover:bg-yellow-100 transition-colors duration-200 no-underline"
                    >
                      <div className="flex items-center space-x-3">
                        <img
                          src={book.cover_image ? `${process.env.REACT_APP_API_URL || 'https://api.silkspace.co'}${book.cover_image}` : '/book-placeholder.png'}
                          alt={book.title}
                          className="h-12 w-9 object-cover rounded-lg border border-yellow-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{book.title}</p>
                          <p className="text-xs text-gray-600 truncate font-medium">by {book.author_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center text-xs bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-semibold">
                        <Eye className="h-3 w-3 mr-1.5" />
                        Review
                      </div>
                    </Link>
                  ))}
                  {pendingBooksData.length > 3 && (
                    <div className="text-center pt-3">
                      <Link 
                        to="/books" 
                        className="text-[#1d1d1d] hover:text-gray-700 text-sm font-semibold transition-colors duration-200 no-underline"
                      >
                        View all {pendingBooksData.length} pending reviews →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-semibold">All caught up! No pending reviews.</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xs border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <Link
                  to="/admin/upload-book"
                  className="text-center p-4 bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white rounded-xl border border-orange-200 hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 no-underline"
                >
                  <Upload className="h-6 w-6 mx-auto mb-2" />
                  <div className="text-sm font-semibold">Upload Book</div>
                </Link>
                <Link
                  to="/books"
                  className="text-center p-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl border border-yellow-200 hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 no-underline"
                >
                  <FileText className="h-6 w-6 mx-auto mb-2" />
                  <div className="text-sm font-semibold">Review Books</div>
                </Link>
                
                {/* Show Users management only for Super Admin */}
                {isSuperAdmin && (
                  <Link
                    to="/users"
                    className="text-center p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl border border-purple-200 hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 no-underline"
                  >
                    <Users className="h-6 w-6 mx-auto mb-2" />
                    <div className="text-sm font-semibold">Manage Users</div>
                  </Link>
                )}
                
                {/* Show Services for all */}
                <Link
                  to="/services"
                  className="text-center p-4 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl border border-indigo-200 hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 no-underline"
                >
                  <Package className="h-6 w-6 mx-auto mb-2" />
                  <div className="text-sm font-semibold">Services</div>
                </Link>
                
                {/* Show Support Tickets for all */}
                <Link
                  to="/support-tickets"
                  className="text-center p-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl border border-red-200 hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 no-underline"
                >
                  <MessageCircle className="h-6 w-6 mx-auto mb-2" />
                  <div className="text-sm font-semibold">Support Tickets</div>
                </Link>
                
                {/* Show Payouts only for Super Admin */}
                {isSuperAdmin && (
                  <Link
                    to="/enhanced-payouts"
                    className="text-center p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl border border-green-200 hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 no-underline"
                  >
                    <DollarSign className="h-6 w-6 mx-auto mb-2" />
                    <div className="text-sm font-semibold">Payouts</div>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}